import { useEffect, useMemo, useRef } from 'react';
import { PureQueryOptions } from '@apollo/client';

import { DashboardConversationsDocument } from 'core/graphql';

import { useGroupRoster } from '../Groups/useGroupRoster';
import {
  IBrowserConversation,
  useConversationsBrowser,
} from './useConversationsBrowser';

/**
 * Communicator middle-column "badge engine": one `conversationsBrowser`
 * sweep over the selected group (channels + workspace DMs + the group's
 * students' conversations incl. gmail), grouped client-side per student,
 * plus the group roster so students without any conversations still get a
 * row. `groupConversations` is NOT usable here — its resolver returns only
 * `slack_channel` rows (ingest never sets `Conversation.group` for DMs).
 */

/** How often the badge queries re-poll as a safety net (covers per-message
 * read/flag mutations that don't refetch conversation aggregates). */
export const BADGE_POLL_INTERVAL_MS = 60000;

/** Drain cap: 1 + MAX_EXTRA_PAGES pages × 20 rows ≈ 200 conversations. A
 * group beyond that undercounts the badges but never request-storms. */
const MAX_EXTRA_PAGES = 9;

export interface ICommsStudentEntry {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  /** False for a workspace-DM counterpart that is not in the group roster. */
  inRoster: boolean;
  terminated: boolean;
  unread: number;
  unanswered: boolean;
}

export interface IGroupCommsIndex {
  students: ICommsStudentEntry[];
  channelsUnread: number;
  channelsUnanswered: boolean;
  /** The group's gmail threads, newest activity first (for the Почта tab). */
  mailThreads: IBrowserConversation[];
  loading: boolean;
  /** Refetch descriptor for the badge sweep — pass to mutations'
   * `refetchQueries` so triage actions update the badges immediately. */
  badgeRefetch: PureQueryOptions;
}

export const useGroupCommsIndex = (groupId: string): IGroupCommsIndex => {
  const { rows: rosterRows, loading: rosterLoading } = useGroupRoster(
    groupId || undefined,
  );

  const {
    conversations,
    hasNextPage,
    loadMore,
    loading: convLoading,
  } = useConversationsBrowser({
    groupId,
    unansweredOnly: false,
    pollInterval: BADGE_POLL_INTERVAL_MS,
  });

  // Bounded auto-drain: badges must sum over ALL of the group's
  // conversations, not the first cursor page.
  const drainedPages = useRef(0);
  useEffect(() => {
    drainedPages.current = 0;
  }, [groupId]);
  useEffect(() => {
    if (!groupId || convLoading || !hasNextPage) return;
    if (drainedPages.current >= MAX_EXTRA_PAGES) return;
    drainedPages.current += 1;
    loadMore();
  }, [groupId, convLoading, hasNextPage, loadMore]);

  const badgeRefetch = useMemo<PureQueryOptions>(
    () => ({
      query: DashboardConversationsDocument,
      variables: {
        search: undefined,
        groupId: groupId || undefined,
        studentId: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        unansweredOnly: false,
        first: 20,
      },
    }),
    [groupId],
  );

  const {
    students,
    channelsUnread,
    channelsUnanswered,
    mailThreads,
  } = useMemo(() => {
    interface ITally {
      unread: number;
      unanswered: boolean;
      firstName: string;
      lastName: string;
    }
    const byStudent = new Map<string, ITally>();
    let chUnread = 0;
    let chUnanswered = false;
    const mail: IBrowserConversation[] = [];

    conversations.forEach((conv) => {
      if (conv.kind === 'gmail_thread') mail.push(conv);
      if (conv.kind === 'slack_channel') {
        chUnread += conv.unreadCount;
        chUnanswered = chUnanswered || conv.isUnanswered;
        return;
      }
      if (!conv.student) return;
      const tally = byStudent.get(conv.student.id) || {
        unread: 0,
        unanswered: false,
        firstName: conv.student.firstName,
        lastName: conv.student.lastName,
      };
      tally.unread += conv.unreadCount;
      tally.unanswered = tally.unanswered || conv.isUnanswered;
      byStudent.set(conv.student.id, tally);
    });

    // Roster first (deduped by student id — a student can hold several
    // agreements); a terminated-only student stays visible when they still
    // have unread messages.
    const entries = new Map<string, ICommsStudentEntry>();
    rosterRows.forEach((row) => {
      const student = row?.student;
      if (!student?.id) return;
      const tally = byStudent.get(student.id);
      const existing = entries.get(student.id);
      const terminated =
        Boolean(row.terminated) && (existing?.terminated ?? true);
      entries.set(student.id, {
        studentId: student.id,
        firstName: student.user?.firstName || '',
        lastName: student.user?.lastName || '',
        email: student.user?.email || '',
        inRoster: true,
        terminated,
        unread: tally?.unread || 0,
        unanswered: tally?.unanswered || false,
      });
    });
    // Workspace-DM counterparts outside the roster still get a row —
    // their messages would otherwise be invisible in the communicator.
    byStudent.forEach((tally, studentId) => {
      if (entries.has(studentId)) return;
      entries.set(studentId, {
        studentId,
        firstName: tally.firstName,
        lastName: tally.lastName,
        email: '',
        inRoster: false,
        terminated: false,
        unread: tally.unread,
        unanswered: tally.unanswered,
      });
    });

    const list = Array.from(entries.values())
      .filter((entry) => !entry.terminated || entry.unread > 0)
      .sort(
        (a, b) =>
          a.lastName.localeCompare(b.lastName) ||
          a.firstName.localeCompare(b.firstName),
      );

    return {
      students: list,
      channelsUnread: chUnread,
      channelsUnanswered: chUnanswered,
      mailThreads: mail,
    };
  }, [conversations, rosterRows]);

  return {
    students,
    channelsUnread,
    channelsUnanswered,
    mailThreads,
    loading: rosterLoading || convLoading,
    badgeRefetch,
  };
};
